"use client";

import React from "react";
import { ArrowRight, CircleAlert, Droplets, Flame, PhoneCall, Pipette, ShieldCheck, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  id: "sewer-repair",
  title: "Sewer Line Repair & Replacement",
  description: "Camera line inspections, hydro jetting, and trenchless sewer pipe repairs to stop backups fast.",
  path: "/services/",
  badge: "24/7 Urgent Dispatch",
  eta: "30-45 Min Arrival",
  iconName: "Pipette",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair_service.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair_service.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/sewer_line_repair_service.png-wvc-sizes",
  alt: "Technician inspecting sewer drainage lines with specialized camera tools"
}, {
  id: "pipe-repair",
  title: "Burst Pipe & Water Leak Repair",
  description: "Fast water shutoff, slab leak detection, and durable pipe replacements to minimize flood damage.",
  path: "/problems/",
  badge: "Immediate Containment",
  eta: "Priority Response",
  iconName: "Droplets",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_leak_repair_technician.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_leak_repair_technician.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_leak_repair_technician.png-wvc-sizes",
  alt: "Plumber fixing damaged copper water supply pipes under pressure"
}, {
  id: "water-heater",
  title: "Emergency Water Heater Service",
  description: "Same-day repair or replacement for leaking tanks, cold water failures, and electrical faults.",
  path: "/water-heater/",
  badge: "All Major Brands",
  eta: "Same-Day Service",
  iconName: "Flame",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_water_heater_install.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_water_heater_install.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_water_heater_install.png-wvc-sizes",
  alt: "Residential water heater unit inspection and valve maintenance"
}, {
  id: "drain-clearing",
  title: "Main Drain Clog Clearing",
  description: "Clearing stubborn blockages in sinks, toilets, main stacks, and outdoor cleanouts safely.",
  path: "/services/",
  badge: "Heavy Duty Snaking",
  eta: "Fast Resolution",
  iconName: "Wrench",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_clearing_auger_service.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_clearing_auger_service.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_clearing_auger_service.png-wvc-sizes",
  alt: "Professional motorized drain snaking equipment resolving a severe clog"
}, {
  id: "toilet-overflow",
  title: "Toilet & Fixture Overflow Relief",
  description: "Emergency stoppage resolution, seal replacements, and backflow prevention to keep homes sanitary.",
  path: "/problems/",
  badge: "Sanitation Priority",
  eta: "On-Call Plumbers",
  iconName: "CircleAlert",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_fixture_overflow_fix.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_fixture_overflow_fix.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/toilet_fixture_overflow_fix.png-wvc-sizes",
  alt: "Emergency plumbing response resolving bathroom fixture flooding"
}, {
  id: "commercial-repairs",
  title: "Commercial & Business Plumbing",
  description: "Rapid plumbing fixes for restaurants, retail units, and office buildings in Clayton County.",
  path: "/contact/",
  badge: "Licensed & Insured",
  eta: "Direct Dispatch",
  iconName: "ShieldCheck",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/commercial_emergency_plumbing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/commercial_emergency_plumbing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/commercial_emergency_plumbing.png-wvc-sizes",
  alt: "Commercial grade water supply and valve plumbing systems"
}];
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Pipette":
        return <Pipette className="size-5 text-primary" />;
      case "Droplets":
        return <Droplets className="size-5 text-primary" />;
      case "Flame":
        return <Flame className="size-5 text-primary" />;
      case "Wrench":
        return <Wrench className="size-5 text-primary" />;
      case "CircleAlert":
        return <CircleAlert className="size-5 text-primary" />;
      case "ShieldCheck":
      default:
        return <ShieldCheck className="size-5 text-primary" />;
    }
  };
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-3 font-semibold tracking-wide">
              Jonesboro Emergency Plumbing Solutions
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold font-default text-foreground tracking-tight">
              Related Emergency Plumbing Services
            </h2>
            <p className="mt-3 text-muted-foreground text-base sm:text-lg">
              Facing another urgent issue? Explore our complete range of rapid-response plumbing repairs across Jonesboro and surrounding Clayton County areas.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="outline" className="h-11 px-5 border-border">
              <Link to="/services/" className="inline-flex items-center gap-2">
                <span>View All Services</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="default" className="h-11 px-5">
              <Link to="/contact/" className="inline-flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call Dispatch Now</span>
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => <motion.div key={service.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-40px"
        }} transition={{
          duration: 0.35,
          delay: index * 0.05
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] bg-card rounded-xl overflow-hidden group">
                <div>
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                    <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-out" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="shadow-xs bg-background/90 text-foreground backdrop-blur-xs font-medium">
                        {service.badge}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-5 pb-2">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-secondary inline-flex items-center justify-center">
                        {getIcon(service.iconName)}
                      </div>
                      <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                        {service.eta}
                      </span>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                </div>

                <CardFooter className="pt-2 pb-5 border-t border-border/60 mt-4">
                  <Button asChild variant="outline" className="w-full justify-between hover:bg-secondary hover:text-secondary-foreground border-border">
                    <Link to={service.path} className="w-full flex items-center justify-between">
                      <span className="font-medium text-sm">Service Details & Dispatch</span>
                      <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-secondary/60 border border-border flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1">
            <h3 className="text-lg sm:text-xl font-bold font-default text-foreground">
              Need immediate help with another plumbing emergency?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              Our experienced plumbers are stationed throughout Jonesboro ready for 24-hour dispatch.
            </p>
          </div>
          <Button asChild size="lg" className="w-full md:w-auto shrink-0 shadow-sm">
            <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
              <PhoneCall className="size-4" />
              <span>Get Immediate Assistance</span>
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}